import { useEffect, useState } from "react";
import { getYggReport, downloadYggReport } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Download, Loader2, TrendingUp, Award, Star } from "lucide-react";

export default function YggReportDialog({ cycleId, open, onOpenChange }) {
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open && cycleId) { setReport(null); getYggReport(cycleId).then(setReport); }
  }, [open, cycleId]);

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await downloadYggReport(cycleId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ygg-rapor-${report?.cycle?.year || ""}.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapor indirildi");
    } catch { toast.error("Rapor indirilemedi"); } finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="ygg-report-dialog">
        <DialogHeader>
          <DialogTitle>YGG Sonuç Raporu {report?.cycle?.name ? `— ${report.cycle.name}` : ""}</DialogTitle>
          <DialogDescription>Yetenek kodu dağılımı, terfi havuzu ve aşama tamamlanma özeti.</DialogDescription>
        </DialogHeader>

        {!report ? (
          <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Rapor hazırlanıyor…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2" data-testid="ygg-report-kpis">
              <Kpi label="Katılımcı" value={report.totals.participants} />
              <Kpi label="Kalibre" value={report.totals.calibrated} />
              <Kpi label="Tamamlanma" value={`%${report.totals.completion_pct}`} />
              <Kpi label="Terfi Havuzu" value={report.promotion_pool.length} />
            </div>

            <div data-testid="ygg-report-distribution">
              <div className="label-eyebrow mb-2">Yetenek Kodu Dağılımı</div>
              {report.talent_distribution.length === 0 ? (
                <div className="text-xs text-slate-400 italic">Henüz kalibrasyon tamamlanmadı.</div>
              ) : (
                <div className="space-y-1.5">
                  {report.talent_distribution.map((d) => (
                    <div key={d.code} className="flex items-center gap-2 text-sm">
                      <span className="w-44 text-slate-700 truncate">{d.label}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2.5">
                        <div className="bg-sky-500 h-2.5 rounded-full" style={{ width: `${d.pct}%` }} />
                      </div>
                      <span className="w-16 text-right text-xs font-semibold">{d.count} (%{d.pct})</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-4 mt-2 text-xs text-slate-500">
                {report.pys_distribution.length > 0 && <span>PYS: {report.pys_distribution.map((p) => `${p.letter}:${p.count}`).join(" · ")}</span>}
                {report.risk_distribution.length > 0 && <span>Risk: {report.risk_distribution.map((p) => `${p.level}:${p.count}`).join(" · ")}</span>}
              </div>
            </div>

            <Pool icon={TrendingUp} title="Terfi Havuzu" items={report.promotion_pool} tone="emerald" testid="ygg-report-promotion-pool" />
            <Pool icon={Star} title="Anahtar Çalışanlar" items={report.key_employees} tone="amber" testid="ygg-report-key-employees" />
            <Pool icon={Award} title="Alanının Uzmanları" items={report.experts} tone="indigo" testid="ygg-report-experts" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Kapat</Button>
          <Button onClick={download} disabled={!report || downloading} data-testid="ygg-report-download">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />} Raporu İndir (HTML)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg border border-sky-100 bg-sky-50 p-3 text-center">
      <div className="text-xl font-bold text-sky-800">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
    </div>
  );
}

function Pool({ icon: Icon, title, items, tone, testid }) {
  const map = { emerald: "text-emerald-700", amber: "text-amber-700", indigo: "text-indigo-700" };
  return (
    <div data-testid={testid}>
      <div className={`text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5 mb-1.5 ${map[tone]}`}><Icon className="w-3.5 h-3.5" /> {title} ({items.length})</div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Kayıt yok.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {items.map((i) => (
            <div key={i.employee_id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-100 bg-slate-50/50">
              {i.avatar ? <img src={i.avatar} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-slate-200" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{i.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{i.role}{i.department ? ` · ${i.department}` : ""}</div>
              </div>
              <div className="text-[10px] text-slate-500 text-right whitespace-nowrap">
                {i.pys && <div>PYS {i.pys}</div>}
                {i.horizon && <div>{i.horizon}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
